import { logout } from 'actions'
import { IS_MAIN_COMPANY } from 'config'
import jwt from 'jwt-decode'
import { AnalyzePdf } from 'pages/Loan'
import CreditDenial from 'pages/Loan/CreditDenial'
import { lazy } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { PermissionGate } from 'utils/PermissionGate'

const FeeWorkSheetPage = lazy(() => import('pages/Loan/FeeWorkSheet'))
const LoanStructure = lazy(() => import('pages/LoanStructure'))
const AddAppraisalInformationV2 = lazy(() => import('pages/Loan/OrderAppraisals/AddAppraisalInformationV2'))
const AppraisalInformation = lazy(() => import('pages/Loan/Underwriting/AppraisalInformation/index'))
const BudgetScopeOfWork = lazy(() => import('pages/Loan/Budget'))
const ByteproLogs = lazy(() => import('pages/Loan/ByteproLogs'))
const ClosingCalendar = lazy(() => import('pages/Loan/ClosingCalendar'))
const ClosingScreen = lazy(() => import('pages/Loan/ClosingScreen'))
const ContractorApplication = lazy(() => import('pages/Loan/ContractorApplication'))
const ContractorApplications = lazy(() => import('pages/Loan/ContractorApplications'))
const Dataverify = lazy(() => import('pages/Loan/Underwriting/Dataverify/Dataverify'))
const DocumentGenie = lazy(() => import('pages/Loan/DocumentGenie/DocumentGenie'))
const ExpirationDates = lazy(() => import('pages/Loan/Underwriting/ExpirationDates'))
const Funding = lazy(() => import('pages/Loan/Funding'))
const Invoices = lazy(() => import('pages/Loan/Invoice/Invoices'))
const LoanChangeRequest = lazy(() => import('pages/Loan/LoanChangeRequest'))
const LoanInTake = lazy(() => import('pages/Loan/LoanInTake'))
const Notes = lazy(() => import('pages/Loan/Servicing/Notes/Notes'))
const Parties = lazy(() => import('pages/Loan/Parties'))
const PostClosing = lazy(() => import('pages/Loan/PostClosing'))
const PurchaseAdvice = lazy(() => import('pages/Loan/PostClosing/PurchaseAdvice'))
const PurchaseAdviceSeller = lazy(() => import('pages/Loan/PostClosing/PurchaseAdviceSeller'))
const ReportDetails = lazy(() => import('pages/Loan/Reports/ReportDetails'))
const Reports = lazy(() => import('pages/Loan/Reports/Reports'))
const ReviewForm = lazy(() => import('pages/Loan/Underwriting/ReviewForm/ReviewForm'))
const SecondaryMarketingScreen = lazy(() => import('pages/Loan/SecondaryMarketing/SecondaryMarketingScreen'))
const Servicing = lazy(() => import('pages/Loan/Servicing/Servicing'))
const Reo = lazy(() => import('pages/Loan/Reo/Reo'))
const Fci = lazy(() => import('pages/Loan/Fci/index'))
const LightningDocs = lazy(() => import('pages/Loan/LightningDocs/index'))
const Underwriting = lazy(() => import('pages/Loan/Underwriting/Underwriting'))
const UnderwritingSummary = lazy(() => import('pages/Loan/Underwriting/UnderwritingSummary'))
const Tasks = lazy(() => import('pages/Tasks/tasks'))
const Appraisals = lazy(() => import('pages/Appraisals'))
const Reminders = lazy(() => import('pages/Reminders'))
const LoanOverview = lazy(() => import('pages/LoanOverview'))
const Pipeline = lazy(() => import('pages/Pipeline'))
const Ratesheet = lazy(() => import('pages/Ratesheet'))
const AdminTools = lazy(() => import('pages/Admin/AdminTools/AdminTools'))
const ConditionsAndTemplates = lazy(() => import('pages/Admin/ConditionsAndTemplates/ConditionsAndTemplates'))
const ManageAccountings = lazy(() => import('pages/Admin/Accounting/ManageAccountings'))
const Mails = lazy(() => import('pages/Admin/Mails'))
const NationalFlood = lazy(() => import('pages/Admin/Vendors/NationalFlood/NationalFlood'))
const UserActivity = lazy(() => import('pages/Admin/UserActivity'))
const Vendors = lazy(() => import('pages/Admin/Vendors'))
const BorrowerDetails = lazy(() => import('pages/Borrower/ManageBorrowers/BorrowerDetails'))
const ManageBorrowers = lazy(() => import('pages/Borrower/ManageBorrowers/ManageBorrowers'))
const EditProfile = lazy(() => import('pages/Auth/EditProfile'))
const AccountDetails = lazy(() => import('pages/ManageAccounts/AccountDetails/AccountDetails'))
const ManageAccounts = lazy(() => import('pages/ManageAccounts/ManageAccounts'))
const ManageTeams = lazy(() => import('pages/ManageTeams/ManageTeams'))
const ManageInvestors = lazy(() => import('pages/Investor/ManageInvestors/ManageInvestors'))
const ManageLoanScenarios = lazy(() => import('pages/Loan/ManageLoanScenarios/ManageLoanScenarios'))
const ExportHistory = lazy(() => import('pages/ManageAccounts/ExportHistory'))
const AmortizationSchedule = lazy(() => import('pages/Loan/AmortizationSchedule/AmortizationSchedule'))
const ManageParticipants = lazy(() => import('pages/Participants/Manage/ManageParticipants'))
const ApplicationTemplate = lazy(() => import('pages/Admin/ConditionsAndTemplates/ApplicationEmailTemplate/detail'))
const Hmda = lazy(() => import('pages/Loan/HMDA/index'))
const BorrowerDrawRequest = lazy(() => import('pages/Borrower/DrawRequest/BorrowerDrawRequest'))
const BorrowingBase = lazy(() => import('pages/Loan/Servicing/BorrowingBase/index'))

const routes = (
  <Switch>
    <Route exact path="/registrations">
      <PermissionGate permission={'MANAGE_ACCOUNTS'} pageId={['manage_users', 'registrations']}>
        <ManageAccounts />
      </PermissionGate>
    </Route>

    <Route exact path="/registrations/exportHistory">
      <PermissionGate permission={'MANAGE_ACCOUNTS'} pageId={['manage_users', 'registrations']}>
        <ExportHistory />
      </PermissionGate>
    </Route>

    <Route exact path="/registrations/:userId">
      <PermissionGate permission={'MANAGE_ACCOUNTS'} pageId={['manage_users', 'registrations']}>
        <AccountDetails />
      </PermissionGate>
    </Route>

    <Route exact path="/teams">
      <PermissionGate permission={'MANAGE_TEAMS'} pageId={['manage_users', 'teams']}>
        <ManageTeams />
      </PermissionGate>
    </Route>

    <Route exact path="/user_activity">
      <PermissionGate permission={'MANAGE_USER_ACTIVITY'} pageId={['manage_users', 'user_activity']}>
        <UserActivity />
      </PermissionGate>
    </Route>
    <Route exact path="/admin_tool/:menu">
      <PermissionGate
        permission={['MANAGE_ADMIN_TOOLS', 'MANAGE_APPRAISER_SETTLEMENT_CONTRACTOR', 'MANAGE_REVIEWS']}
        pageId={['admin_tool', ':menu']}
      >
        <AdminTools />
      </PermissionGate>
    </Route>
    <Route exact path="/accountings/:menu">
      <PermissionGate permission={['MANAGE_ACCOUNTING']} pageId={['manage_accountings', ':menu']}>
        <ManageAccountings />
      </PermissionGate>
    </Route>
    <Route exact path="/mails">
      <PermissionGate permission={['MANAGE_MAILS']}>
        <Mails />
      </PermissionGate>
    </Route>
    <Route exact path="/vendors/:menu">
      <PermissionGate permission={'MANAGE_VENDORS'} pageId={['vendors']}>
        <Vendors />
      </PermissionGate>
    </Route>
    <Route exact path="/condition_template/applicationEmailTemplate/:id">
      <PermissionGate permission={'MANAGE_CONDITIONS_TEMPLATES'}>
        <ApplicationTemplate />
      </PermissionGate>
    </Route>
    <Route exact path="/condition_template/:menu">
      <PermissionGate permission={'MANAGE_CONDITIONS_TEMPLATES'} pageId={['condition_template', ':menu']}>
        <ConditionsAndTemplates />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing/:loan_number">
      <PermissionGate pageId={['pipeline', 'servicing', ':menu']}>
        <Servicing />
      </PermissionGate>
    </Route>
    <Route exact path="/reo/:loan_number">
      <Reo />
    </Route>
    <Route exact path="/budget/:loanNumber">
      <PermissionGate permission={'BUDGET_SCOPE_OF_WORK'} pageId={['pipeline', 'budget']}>
        <BudgetScopeOfWork />
      </PermissionGate>
    </Route>
    <Route exact path="/underwriting/:loan_number">
      <PermissionGate pageId={['pipeline', 'underwriting']}>
        <Underwriting />
      </PermissionGate>
    </Route>
    <Route exact path="/loanInTake/:loan_number">
      <PermissionGate pageId={['pipeline', 'loanInTake']}>
        <LoanInTake />
      </PermissionGate>
    </Route>
    <Route exact path="/bytepro_logs/:loan_number">
      <PermissionGate pageId={['pipeline', 'bytepro_logs']}>
        <ByteproLogs />
      </PermissionGate>
    </Route>
    <Route exact path="/edit_profile">
      <EditProfile />
    </Route>
    <Route exact path="/loan_process/:action/:loan_number">
      <LoanOverview />
    </Route>
    <Route exact path="/pool/:id">
      <LoanStructure />
    </Route>
    <Route exact path="/pool/parties/:id">
      <Parties />
    </Route>
    <Route exact path="/loan_change_request/:loan_number">
      <PermissionGate pageId={['pipeline', 'loan_change_request']}>
        <LoanChangeRequest />
      </PermissionGate>
    </Route>
    <Route exact path="/post_closing/:loan_number">
      <PermissionGate pageId={['pipeline', 'post_closing']}>
        <PostClosing />
      </PermissionGate>
    </Route>
    <Route exact path="/closing_calendar/:loan_number">
      <PermissionGate pageId={['pipeline', 'closing_calendar']}>
        <ClosingCalendar />
      </PermissionGate>
    </Route>
    <Route exact path="/closing_screen/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'} pageId={['pipeline', 'closing_screen', 'menu']}>
        <ClosingScreen />
      </PermissionGate>
    </Route>
    <Route exact path="/fee_worksheet/:loan_number">
      <PermissionGate pageId={['pipeline', 'fee_worksheet']}>
        <FeeWorkSheetPage />
      </PermissionGate>
    </Route>
    <Route exact path="/amortization_schedule/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'} pageId={['pipeline', 'amortization_schedule']}>
        <AmortizationSchedule />
      </PermissionGate>
    </Route>
    <Route exact path="/order_valuation/:loan_number">
      <PermissionGate pageId={['pipeline', 'order_valuation']}>
        <AddAppraisalInformationV2 />
      </PermissionGate>
    </Route>
    <Route exact path="/document_genie/:loan_number">
      <PermissionGate pageId={['pipeline', 'document_genie']}>
        <DocumentGenie />
      </PermissionGate>
    </Route>
    <Route exact path="/review_form/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'} pageId={['pipeline', 'review_form']}>
        <ReviewForm />
      </PermissionGate>
    </Route>
    <Route exact path="/appraisal_information/:loan_number">
      <PermissionGate permission={'MANAGE_APPRAISAL_INFORMATION'} pageId={['pipeline', 'appraisal_information']}>
        <AppraisalInformation />
      </PermissionGate>
    </Route>
    <Route exact path="/reminders">
      <PermissionGate pageId={['reminders']}>
        <Reminders />
      </PermissionGate>
    </Route>
    <Route exact path="/reminders/:loanNumber">
      <PermissionGate pageId={['reminders']}>
        <Reminders />
      </PermissionGate>
    </Route>
    <Route exact path="/appraisals">
      <PermissionGate permission={'MANAGE_APPRAISAL_INFORMATION'} pageId={['appraisals']}>
        <Appraisals />
      </PermissionGate>
    </Route>
    <Route exact path="/expiration_dates/:loan_number">
      <PermissionGate pageId={['expiration_dates']}>
        <ExpirationDates />
      </PermissionGate>
    </Route>
    <Route exact path="/notes/:loan_number">
      <PermissionGate permission={'CAN_VIEW_NOTES'} pageId={['notes']}>
        <Notes />
      </PermissionGate>
    </Route>
    <Route exact path="/dataverify/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'}>
        <Dataverify />
      </PermissionGate>
    </Route>
    <Route exact path="/underwriting_summary/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'} pageId={['underwriting_summary']}>
        <UnderwritingSummary />
      </PermissionGate>
    </Route>
    <Route exact path="/purchase_advice/:loan_number">
      <PermissionGate permission={'PURCHASE_ADVICE_BUYER'}>
        <PurchaseAdvice />
      </PermissionGate>
    </Route>
    <Route exact path="/purchase_advice_seller/:loan_number">
      <PermissionGate permission={'PURCHASE_ADVICE_SELLER'}>
        <PurchaseAdviceSeller />
      </PermissionGate>
    </Route>
    <Route exact path="/secondary_marketing_screen/:loan_number">
      <PermissionGate permission={'MANAGE_SECONDARY_MARKETING'} pageId={['pipeline', 'secondary_marketing_screen']}>
        <SecondaryMarketingScreen />
      </PermissionGate>
    </Route>
    <Route exact path="/national_flood/:loan_number">
      <PermissionGate permission={'ADMIN_TO_AE_PROFILE_PERMISSION'}>
        <NationalFlood />
      </PermissionGate>
    </Route>
    <Route exact path="/reports">
      <PermissionGate permission={'MANAGE_REPORTS'} pageId={['reports']}>
        <Reports />
      </PermissionGate>
    </Route>
    <Route exact path="/reports/servicing">
      <PermissionGate permission={'MANAGE_REPORTS'} pageId={['reports']}>
        <Reports origin={1} />
      </PermissionGate>
    </Route>
    <Route exact path="/reports/:id">
      <PermissionGate permission={'MANAGE_REPORTS'} pageId={['reports']}>
        <ReportDetails />
      </PermissionGate>
    </Route>
    <Route exact path="/pipeline">
      <PermissionGate pageId={['pipeline']}>
        <Pipeline />
      </PermissionGate>
    </Route>
    <Route exact path="/ratesheet">
      <PermissionGate pageId={['manage_users', 'ratesheet']}>
        <Ratesheet />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks">
      <PermissionGate permission={'MANAGE_TASKS'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/:projectId">
      <PermissionGate permission={'MANAGE_TASKS'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/loan/:loanNumber">
      <PermissionGate permission={'MANAGE_TASKS'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/loan/:loanNumber/:taskNo">
      <PermissionGate permission={'MANAGE_TASKS'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/parties/:loanNumber">
      <PermissionGate permission={['MANAGE_PARTIES', 'MANAGE_PARTIES_INSURANCE_TITLE']}>
        <Parties />
      </PermissionGate>
    </Route>
    <Route exact path="/funding/:loanNumber">
      <PermissionGate permission={'MANAGE_FUNDING'} pageId={['pipeline', 'funding']}>
        <Funding />
      </PermissionGate>
    </Route>
    <Route exact path="/contractor_application/:loanNumber">
      <PermissionGate pageId={['pipeline', 'contractor_application']}>
        <ContractorApplications />
      </PermissionGate>
    </Route>
    <Route exact path="/contractor_application/:no/:loanNumber">
      <PermissionGate pageId={['pipeline', 'contractor_application']}>
        <ContractorApplication />
      </PermissionGate>
    </Route>
    <Route exact path="/invoice/:loanNumber">
      <PermissionGate permission={'INVOICE'} pageId={['pipeline', 'invoice']}>
        <Invoices />
      </PermissionGate>
    </Route>
    <Route exact path="/borrowers">
      <PermissionGate permission={'MANAGE_BORROWERS'} pageId={['manage_users', 'borrowers']}>
        <ManageBorrowers />
      </PermissionGate>
    </Route>
    <Route exact path="/borrowers/:borrowerId">
      <PermissionGate permission={'MANAGE_BORROWERS'} pageId={['manage_users', 'borrowers']}>
        <BorrowerDetails />
      </PermissionGate>
    </Route>
    <Route exact path="/manageInvestors">
      <PermissionGate permission={'MANAGE_INVESTORS'} pageId={['manage_users', 'manageInvestors']}>
        <ManageInvestors />
      </PermissionGate>
    </Route>
    <Route exact path="/manageParticipants">
      <PermissionGate permission={'MANAGE_PARTICIPANTS'} pageId={['manage_users', 'manageParticipants']}>
        <ManageParticipants />
      </PermissionGate>
    </Route>
    <Route exact path="/loanScenarios">
      <PermissionGate permission={'MANAGE_SCENARIOS'} pageId={['manage_users', 'loanScenarios']}>
        <ManageLoanScenarios />
      </PermissionGate>
    </Route>
    <Route exact path="/fci/:loan_number">
      <PermissionGate pageId={['pipeline', 'fci']}>
        <Fci />
      </PermissionGate>
    </Route>
    <Route exact path="/lightningDocs/:loan_number">
      <PermissionGate pageId={['pipeline', 'lightningDocs']}>
        <LightningDocs />
      </PermissionGate>
    </Route>
    <Route exact path="/hmda/:loan_number">
      <Hmda />
    </Route>
    <Route exact path="/creditDenial/:loan_number">
      <CreditDenial />
    </Route>
    <Route exact path="/analyze/:loan_number">
      <AnalyzePdf />
    </Route>
    <Route exact path="/drawRequest/:loanNumber" key={11}>
      <BorrowerDrawRequest forBorrower={false} />
    </Route>
    <Route exact path="/borrowingBase/:loanNumber" key={11}>
      <BorrowingBase />
    </Route>
    <Route path="*">
      <Redirect to={IS_MAIN_COMPANY ? '/home' : '/pipeline'} />
    </Route>
  </Switch>
)

export const ProtectedRoutes = () => {
  const dispatch = useDispatch()
  const auth = useSelector((state: any) => state.auth)
  let success = false

  try {
    const payload: any = jwt(auth.token)
    if (payload.exp * 1000 > Date.now()) {
      success = true
    } else {
      dispatch(logout())
    }
  } catch {}

  try {
    if (auth.profile.temparary) {
      dispatch(logout())
      success = false
    }
  } catch {}

  const location = useLocation()

  if (success) return routes

  return (
    <Redirect
      to={{
        pathname: IS_MAIN_COMPANY ? '/home' : '/login',
        state: { from: location.pathname }, // Save the original URL
      }}
    />
  )
}
